import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Slider,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

import ReactCrop, {
  centerCrop as centerReactCrop,
  Crop,
  makeAspectCrop,
  PixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

interface Props {
  open: boolean;
  image: string;
  fileName: string;
  onClose: () => void;
  onComplete: (croppedFile: File) => void;
}

interface Size {
  width: number;
  height: number;
}

const CROP_ASPECT = 1;

function centerSquareCrop(imgW: number, imgH: number): Crop {
  return centerReactCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 60,
      },
      CROP_ASPECT,
      imgW,
      imgH,
    ),
    imgW,
    imgH,
  );
}

function getCropCenteredPan(
  crop: Crop | undefined,
  imageWidth: number,
  imageHeight: number,
) {
  if (!crop) return { x: 0, y: 0 };

  const pixelCrop = toPixelCrop(crop, imageWidth, imageHeight);
  return {
    x: imageWidth / 2 - (pixelCrop.x + pixelCrop.width / 2),
    y: imageHeight / 2 - (pixelCrop.y + pixelCrop.height / 2),
  };
}

function canStartPan(target: EventTarget | null) {
  if (!(target instanceof Element)) return true;

  return !target.closest(
    ".ReactCrop__crop-selection, .ReactCrop__drag-handle, .ReactCrop__drag-bar",
  );
}

async function cropToFile(
  img: HTMLImageElement,
  crop: PixelCrop,
  fileName: string,
  mime: string = "image/jpeg",
  quality: number = 0.9,
) {
  const scaleX = img.naturalWidth / img.width;
  const scaleY = img.naturalHeight / img.height;

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(crop.width * scaleX));
  canvas.height = Math.max(1, Math.floor(crop.height * scaleY));

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context not available");

  ctx.drawImage(
    img,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, mime, quality),
  );

  if (!blob) throw new Error("Failed to create blob");

  const originalNameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
  return new File([blob], `${originalNameWithoutExt}.jpeg`, { type: mime });
}

function toPixelCrop(
  crop: Crop,
  imageWidth: number,
  imageHeight: number,
): PixelCrop {
  if (crop.unit === "%") {
    return {
      unit: "px",
      x: Math.round((crop.x / 100) * imageWidth),
      y: Math.round((crop.y / 100) * imageHeight),
      width: Math.round((crop.width / 100) * imageWidth),
      height: Math.round((crop.height / 100) * imageHeight),
    };
  }

  return {
    unit: "px",
    x: Math.round(crop.x),
    y: Math.round(crop.y),
    width: Math.round(crop.width),
    height: Math.round(crop.height),
  };
}

export const ImageCropDialog: React.FC<Props> = ({
  open,
  image,
  fileName,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation();

  const imgRef = useRef<HTMLImageElement | null>(null);

  const [crop, setCrop] = useState<Crop>();
  const [baseImageSize, setBaseImageSize] = useState<Size | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{
    mx: number;
    my: number;
    px: number;
    py: number;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setCrop(undefined);
      setBaseImageSize(null);
    }
  }, [open, image]);

  const handleZoomChange = (_: Event, v: number | number[]) => {
    const newZoom = v as number;
    setZoom(newZoom);
    if (newZoom === 1 || !baseImageSize) {
      setPan({ x: 0, y: 0 });
      return;
    }

    setPan(
      getCropCenteredPan(
        crop,
        baseImageSize.width * newZoom,
        baseImageSize.height * newZoom,
      ),
    );
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1 || !canStartPan(e.target)) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragStart.current) return;
    setPan({
      x: dragStart.current.px + (e.clientX - dragStart.current.mx),
      y: dragStart.current.py + (e.clientY - dragStart.current.my),
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    dragStart.current = null;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (zoom <= 1 || !canStartPan(e.target)) return;
    const touch = e.touches[0];
    setIsDragging(true);
    dragStart.current = {
      mx: touch.clientX,
      my: touch.clientY,
      px: pan.x,
      py: pan.y,
    };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !dragStart.current) return;
    const touch = e.touches[0];
    setPan({
      x: dragStart.current.px + (touch.clientX - dragStart.current.mx),
      y: dragStart.current.py + (touch.clientY - dragStart.current.my),
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    dragStart.current = null;
  };

  const handleUseFullImage = async () => {
    const res = await fetch(image);
    const blob = await res.blob();
    const originalNameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
    const fullImageFile = new File([blob], `${originalNameWithoutExt}.jpeg`, {
      type: "image/jpeg",
    });
    onComplete(fullImageFile);
  };

  const handleApply = async () => {
    try {
      if (!imgRef.current) return;

      const cropToUse = crop
        ? toPixelCrop(crop, imgRef.current.width, imgRef.current.height)
        : null;

      if (!cropToUse || cropToUse.width <= 0 || cropToUse.height <= 0) return;

      const file = await cropToFile(imgRef.current, cropToUse, fileName);
      onComplete(file);
    } catch (error) {
      console.error("Failed to crop image:", error);
    }
  };

  const imageDisplaySize = baseImageSize
    ? {
        width: baseImageSize.width * zoom,
        height: baseImageSize.height * zoom,
      }
    : null;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          marginBottom: "10px",
          marginTop: 0,
          marginLeft: 0,
          marginRight: "10px",
          flexShrink: 0,
        }}
      >
        {t("imageCropDialog.adjustImage")}
        <IconButton
          onClick={onClose}
          sx={{
            transition: "all 0.2s ease",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#0d47a1",
              transform: "scale(1.06)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          background: "#111",
          p: 2,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
        }}
      >
        <Box
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
            userSelect: "none",
            touchAction: "none",
          }}
        >
          <Box
            sx={{
              transform: `translate(${pan.x}px, ${pan.y}px)`,
              transition: isDragging ? "none" : "transform 1.0s ease",
              lineHeight: 0,
            }}
          >
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              aspect={CROP_ASPECT}
              keepSelection
              style={{ maxWidth: "none" }}
            >
              <img
                ref={imgRef}
                alt="Crop"
                src={image}
                crossOrigin="anonymous"
                onLoad={(e) => {
                  const img = e.currentTarget;
                  const renderedSize = {
                    width: img.width,
                    height: img.height,
                  };
                  const initialCrop = centerSquareCrop(
                    renderedSize.width,
                    renderedSize.height,
                  );
                  setBaseImageSize(renderedSize);
                  setCrop(initialCrop);
                  setPan({ x: 0, y: 0 });
                }}
                style={{
                  display: "block",
                  maxWidth: imageDisplaySize ? "none" : "100%",
                  maxHeight: imageDisplaySize ? "none" : "45vh",
                  width: imageDisplaySize
                    ? `${imageDisplaySize.width}px`
                    : "auto",
                  height: imageDisplaySize
                    ? `${imageDisplaySize.height}px`
                    : "auto",
                }}
              />
            </ReactCrop>
          </Box>
        </Box>
      </DialogContent>

      <Box
        sx={{
          p: 3,
          borderTop: "1px solid #eee",
          background: "#fff",
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("imageCropDialog.zoom")}
        </Typography>

        <Slider
          value={zoom}
          min={1}
          max={3}
          step={0.1}
          onChange={handleZoomChange}
          sx={{ color: "#0d47a1", mb: 2 }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            marginBottom: 0,
            marginTop: 0,
            marginLeft: "-10px",
            marginRight: 0,
          }}
        >
          <Button className="btn btn-primary" onClick={handleUseFullImage}>
            {t("imageCropDialog.useFullImage")}
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={onClose} className="btn btn-no-border">
            {t("dialogReport.cancel")}
          </Button>
          <Button onClick={handleApply} className="btn btn-primary">
            {t("imageCropDialog.apply")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
